import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { Columns3, LucideIconData, Trash2, X } from 'lucide-angular';

import { CompareService } from '../../services/compare.service';
import { Product } from '../../models/product.model';
import { ButtonComponent } from '../button/button.component';
import { IconComponent } from '../icon/icon.component';

@Component({
  selector: 'app-compare-bar',
  imports: [ButtonComponent, IconComponent],
  templateUrl: './compare-bar.component.html',
  styleUrl: './compare-bar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompareBarComponent {
  private readonly compareService = inject(CompareService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly items = this.compareService.items;
  protected readonly ids = this.compareService.ids;
  protected readonly count = this.compareService.count;
  protected readonly maxItems = this.compareService.maxItems;

  protected readonly compareIcon: LucideIconData = Columns3;
  protected readonly clearIcon: LucideIconData = Trash2;
  protected readonly closeIcon: LucideIconData = X;

  private readonly currentUrl = signal(this.router.url);

  protected readonly isOnComparePage = computed(() =>
    this.currentUrl().split('?')[0].startsWith('/comparar'),
  );

  protected readonly show = computed(
    () => this.count() > 0 && !this.isOnComparePage(),
  );

  protected readonly slots = computed(() => {
    const order = this.ids();
    const byId = new Map(this.items().map((p) => [Number(p.id), p]));
    const filled: (Product | null)[] = order.map((id) => byId.get(id) ?? null);
    while (filled.length < this.maxItems) filled.push(null);
    return filled;
  });

  protected readonly canCompare = computed(() => this.count() >= 2);

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((e) => this.currentUrl.set(e.urlAfterRedirects || e.url));
  }

  protected onCompare(): void {
    if (!this.canCompare()) return;
    const ids = this.ids().join(',');
    this.router.navigate(['/comparar'], { queryParams: { ids } });
  }

  protected onClear(): void {
    this.compareService.clear();
  }

  protected onRemove(id: number | string): void {
    this.compareService.removeById(id);
  }
}
